import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Detects new deploys and reloads the SPA so users don't get stuck on an old
 * bundle. A build-stamped /version.json (see scripts/gen-version.js) is polled;
 * when its value changes vs. the version loaded with this session, a reload is
 * armed and fired at the next safe moment — a route change or a tab refocus —
 * so we never interrupt someone mid-interaction. Renders nothing.
 */
const POLL_MS = 5 * 60 * 1000; // 5 minutes

const AutoUpdater: React.FC = () => {
  const location = useLocation();
  const loadedVersion = useRef<string | null>(null);
  const updateReady = useRef(false);

  const fetchVersion = useCallback(async (): Promise<string | null> => {
    try {
      const res = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' });
      if (!res.ok) return null;
      const data = await res.json();
      return typeof data?.version === 'string' ? data.version : null;
    } catch {
      return null;
    }
  }, []);

  const check = useCallback(async (): Promise<boolean> => {
    const v = await fetchVersion();
    if (!v) return false;
    if (loadedVersion.current === null) {
      loadedVersion.current = v; // first read = the version this session started on
      return false;
    }
    if (v !== loadedVersion.current) {
      updateReady.current = true;
      return true;
    }
    return false;
  }, [fetchVersion]);

  useEffect(() => {
    check();
    const interval = setInterval(check, POLL_MS);
    const onFocus = () => {
      check().then((changed) => {
        if (changed || updateReady.current) window.location.reload();
      });
    };
    const onVisibility = () => { if (document.visibilityState === 'visible') onFocus(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [check]);

  // A route change is a natural break point: if a newer build is live, reload
  // now so the next page renders from the fresh bundle.
  useEffect(() => {
    if (updateReady.current) window.location.reload();
  }, [location.pathname]);

  return null;
};

export default AutoUpdater;
